/**
 * Risk Selector Dialog
 *
 * Allows user to select which risk a mitigation should be linked to
 * when no risk context is available.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import type { Risk, AssistantSuggestion } from 'shared';

// =============================================================================
// TYPES
// =============================================================================

interface RiskSelectorDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (riskId: string) => void;
  risks: Risk[];
  mitigationSuggestion: AssistantSuggestion | null;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function getRiskLevelColor(
  score: number
): 'error' | 'warning' | 'info' | 'success' {
  if (score >= 7) return 'error';
  if (score >= 5) return 'warning';
  if (score >= 3) return 'info';
  return 'success';
}

function getRiskLevelLabel(score: number, t: (key: string) => string): string {
  if (score >= 7) return t('risk.level.high');
  if (score >= 5) return t('risk.level.medium');
  if (score >= 3) return t('risk.level.low');
  return t('risk.level.minimal');
}

// =============================================================================
// COMPONENT
// =============================================================================

export const RiskSelectorDialog: React.FC<RiskSelectorDialogProps> = ({
  open,
  onClose,
  onSelect,
  risks,
  mitigationSuggestion,
}) => {
  const { t } = useTranslation();

  const handleSelectRisk = (riskId: string) => {
    onSelect(riskId);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SecurityIcon color="primary" />
        {t('assistant.riskSelector.title')}
      </DialogTitle>

      <DialogContent>
        {mitigationSuggestion && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t('assistant.riskSelector.mitigationToCreate')}
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {mitigationSuggestion.title}
            </Typography>
            {mitigationSuggestion.description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {mitigationSuggestion.description.substring(0, 150)}
                {mitigationSuggestion.description.length > 150 ? '...' : ''}
              </Typography>
            )}
          </Box>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('assistant.riskSelector.description')}
        </Typography>

        {risks.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <WarningIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">
              {t('assistant.riskSelector.noRisks')}
            </Typography>
          </Box>
        ) : (
          <List sx={{ pt: 0 }}>
            {risks.map(risk => {
              const score =
                Number(risk.residualRisk) || Number(risk.inherentRisk) || 0;
              return (
                <ListItemButton
                  key={risk.id}
                  onClick={() => handleSelectRisk(risk.id)}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'primary.50',
                    },
                  }}
                >
                  <ListItemIcon>
                    <WarningIcon color={getRiskLevelColor(score)} />
                  </ListItemIcon>
                  <ListItemText
                    primary={risk.title}
                    secondary={
                      <Box
                        component="span"
                        sx={{ display: 'flex', gap: 1, mt: 0.5 }}
                      >
                        <Chip
                          size="small"
                          label={getRiskLevelLabel(score, t)}
                          color={getRiskLevelColor(score)}
                          variant="outlined"
                        />
                        {risk.threatSource && (
                          <Typography variant="caption" color="text.secondary">
                            {risk.threatSource.substring(0, 50)}
                            {risk.threatSource.length > 50 ? '...' : ''}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RiskSelectorDialog;
